import { useEffect, useState } from "react";
import { Plug, Send, Mail, CheckCircle2, AlertCircle, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Link } from "react-router-dom";
import { notifyError } from "@/lib/errorToast";

type ConnStatus = "unknown" | "connected" | "disconnected" | "checking";

const CONNECTORS = [
  { id: "slack", label: "Slack", icon: Send, fn: "slack-post", listFn: "slack-list", desc: "Mensagens em canais Slack" },
  { id: "google_mail", label: "Gmail", icon: Mail, fn: "gmail-send", listFn: "gmail-list", desc: "Enviar e listar emails" },
] as const;

export default function Connections() {
  const { user } = useAuth();
  const [status, setStatus] = useState<Record<string, ConnStatus>>({});

  useEffect(() => {
    if (!user) return;
    probe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function probe() {
    const next: Record<string, ConnStatus> = {};
    for (const c of CONNECTORS) next[c.id] = "checking";
    setStatus(next);
    await Promise.all(
      CONNECTORS.map(async (c) => {
        try {
          const { data, error } = await supabase.functions.invoke(c.fn, { body: {} });
          const msg = (error as unknown as { context?: { body?: string } })?.context?.body ?? (data as { error?: string })?.error ?? "";
          const txt = typeof msg === "string" ? msg : JSON.stringify(msg);
          next[c.id] = txt.toLowerCase().includes("não conectado") ? "disconnected" : "connected";
        } catch {
          next[c.id] = "disconnected";
        }
      }),
    );
    setStatus({ ...next });
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
              <ArrowLeft className="w-3 h-3" /> Voltar
            </Link>
            <h1 className="text-2xl font-display font-bold flex items-center gap-2">
              <Plug className="w-6 h-6 text-primary" /> Connections
            </h1>
            <p className="text-sm text-muted-foreground">
              Status dos conectores OAuth. Use o chat da Lovable: <span className="font-mono">"conecte o Slack"</span> ou{" "}
              <span className="font-mono">"conecte o Gmail"</span>.
            </p>
          </div>
          <Button onClick={probe} variant="outline" size="sm" aria-label="Re-checar status">
            Re-checar
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {CONNECTORS.map((c) => {
            const Icon = c.icon;
            const s = status[c.id] ?? "unknown";
            return (
              <div key={c.id} className="glass-panel p-5 rounded-lg border border-primary/20 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Icon className="w-4 h-4 text-primary" /> {c.label}
                  </h3>
                  <StatusBadge status={s} />
                </div>
                <p className="text-xs text-muted-foreground">{c.desc}</p>
                <div className="text-[10px] font-mono text-muted-foreground">
                  Edge functions: <span className="text-primary">{c.fn}</span> · <span className="text-primary">{c.listFn}</span>
                </div>
                {s === "disconnected" && (
                  <div className="text-xs p-2.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">
                    Não conectado neste workspace. Peça à IA da Lovable para conectar.
                  </div>
                )}
                {s === "connected" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const { data, error } = await supabase.functions.invoke(c.listFn, { body: {} });
                        if (error) throw error;
                        const items = (data as { channels?: unknown[]; messages?: unknown[] }).channels ?? (data as { channels?: unknown[]; messages?: unknown[] }).messages ?? [];
                        alert(`${c.label}: ${items.length} itens encontrados (ver console)`);
                        // eslint-disable-next-line no-console
                        console.log(`[${c.label}]`, items);
                      } catch (e) {
                        notifyError(e as Error, `Falha ao listar ${c.label}`);
                      }
                    }}
                  >
                    Testar listagem
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: ConnStatus }) {
  if (status === "connected") {
    return (
      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-success/10 text-success border border-success/30 flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" /> conectado
      </span>
    );
  }
  if (status === "disconnected") {
    return (
      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" /> não conectado
      </span>
    );
  }
  if (status === "checking") {
    return (
      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/30 flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" /> checando
      </span>
    );
  }
  return <span className="text-[10px] font-mono text-muted-foreground">—</span>;
}
